import React from "react";
import { FaFingerprint, FaCheckDouble, FaWallet } from "react-icons/fa";

interface ProcessProps {
  sectionRef?: (el: HTMLElement | null) => void;
}

const Process: React.FC<ProcessProps> = ({ sectionRef }) => {
  return (
    <section ref={sectionRef} className="w-full py-32 bg-transparent relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-grid-pattern opacity-5 -z-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
        <div className="flex flex-col items-center gap-6 mb-24">
          <div className="text-secondary font-black text-[10px] uppercase tracking-[0.4em] bg-secondary/10 px-6 py-2 rounded-full border border-secondary/20">
            The Journey
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-text-primary">
            How it <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Works.</span>
          </h2>
          <p className="text-text-secondary text-lg md:text-xl font-medium max-w-2xl opacity-80">
            Get your funds in three simple, transparent steps. No hidden complexity, just pure digital efficiency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 relative">
          {/* Connector Line (Desktop) */}
          <div className="hidden md:block absolute top-1/4 left-[10%] right-[10%] h-0.5 bg-gray-100 -z-0">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary to-transparent animate-shimmer bg-[length:200%_auto]" />
          </div>

          {[
            {
              step: "01",
              title: "Digital Application",
              desc: "Complete our secure, 1-minute basic info form from any device, anywhere.",
              icon: <FaFingerprint />,
              color: "var(--primary)"
            },
            {
              step: "02",
              title: "Instant Verification",
              desc: "Our AI-driven verification engine processes your documents in real-time for rapid approval.",
              icon: <FaCheckDouble />,
              color: "var(--secondary)"
            },
            {
              step: "03",
              title: "Direct Disbursal",
              desc: "Once confirmed, funds are transferred directly to your bank account within 24 hours.",
              icon: <FaWallet />,
              color: "var(--primary)"
            },
          ].map((item, i) => (
            <div key={i} className="relative z-10 flex flex-col items-center group">
              <div className="relative mb-10">
                <div 
                  className="w-24 h-24 glass-morphism border border-white/50 shadow-2xl rounded-3xl flex items-center justify-center text-4xl text-primary transition-all duration-700 group-hover:scale-110 group-hover:rotate-3"
                  style={{ boxShadow: `0 20px 40px -10px color-mix(in srgb, ${item.color} 20%, transparent)` }}
                >
                  {item.icon}
                </div>
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-text-primary text-white text-[10px] font-black flex items-center justify-center rounded-xl border-4 border-white shadow-xl">
                  {item.step}
                </div>
              </div>
              
              <h3 className="text-2xl font-black mb-4 text-text-primary tracking-tight group-hover:text-primary transition-colors">
                {item.title}
              </h3>
              <p className="text-text-secondary font-medium leading-relaxed max-w-xs opacity-70 group-hover:opacity-100 transition-opacity">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
